import {
  Flex,
  StylesProvider,
  useColorModeValue,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import { ControlProps, GroupBase } from "react-select";
import { Option as IntentsifyOption } from "@intentsify/types";
import { byLook } from "theme/look";

export const SimpleControl = <
  Option extends IntentsifyOption,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  innerProps,
  isFocused,
  isDisabled,
  innerRef,
  children,
  selectProps,
}: ControlProps<Option, IsMulti, Group>) => {
  const inputStyles = useMultiStyleConfig("Input", {});
  const legacyBorderColor = useColorModeValue(
    "whiteAlpha.400",
    "whiteAlpha.300",
  );
  // The brand-look header follows the colour mode (white in light mode).
  const brandBorderColor = useColorModeValue("gray.300", "whiteAlpha.300");
  const brandActiveBorderColor = useColorModeValue(
    "gray.500",
    "whiteAlpha.600",
  );
  const brandTextColor = useColorModeValue("gray.800", "white");

  const borderColor = byLook(brandBorderColor, legacyBorderColor);
  const activeBorderColor = byLook(brandActiveBorderColor, "whiteAlpha.600");
  const textColor = byLook(brandTextColor, "white");

  return (
    <StylesProvider value={inputStyles}>
      <Flex
        ref={innerRef}
        sx={{
          p: 0,
          overflow: "hidden",
          h: "auto",
          border: "1px solid",
          w: "200px",
          borderRadius: "md",
          borderColor,
          minH: 4,
          color: textColor,
          fontWeight: "medium",
          _focus: {
            borderColor: activeBorderColor,
            color: textColor,
          },
          _active: {
            borderColor: activeBorderColor,
            color: textColor,
          },
          _hover: {
            borderColor: activeBorderColor,
          },
        }}
        {...innerProps}
        {...(isFocused && { "data-focus": true })}
        {...(isDisabled && { disabled: true })}
        aria-invalid={selectProps["aria-invalid"]}
      >
        {children}
      </Flex>
    </StylesProvider>
  );
};
